import React, {
  memo,
  type ReactElement,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { Logger } from '@saviynt/common';
import {
  Box,
  Button,
  ButtonIcon,
  Icon,
  Loader,
  // Link as DSLink,
  Menu,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';

import { useGetWidget } from '../../../features/ViolationsTab/hooks';
import {
  CF_CRT_MED,
  CF_INF_MED,
  CF_NEU_BLD,
  CF_NEU_STL,
  CF_SEC_BLD,
} from '../../constants';
import { useInvertedLocalize, useTypedSelector } from '../../hooks';
import {
  type ActiveWidgetItem,
  type WidgetDataListItem,
  type WidgetMenuItem,
} from '../../types';

import SodWidgetListItem from './SodWidgetListItem';

import './SodWidget.css';

const ALLOWED_COLUMN_SPANS = [3, 4, 6, 8, 9] as const;

export const WIDGET_KINDS: Record<string, string> = {
  REGULAR: 'regular',
  USER: 'user',
  ENDPOINT: 'endpoint',
  ENTITLEMENT: 'entitlement',
} as const;

const WIDGET_ACTION: Record<
  string,
  {
    label: string;
    value: string;
    icon: string;
  }
> = {
  HIDE: {
    label: 'Hide',
    value: 'HIDE',
    icon: 'VisibilityOff',
  },
  CUSTOMIZE: {
    label: 'Customize',
    value: 'CUSTOMIZE',
    icon: 'Settings',
  },
} as const;

type AllowedSpan = (typeof ALLOWED_COLUMN_SPANS)[number];
type WidgetAction = (typeof WIDGET_ACTION)[keyof typeof WIDGET_ACTION];

interface SodWidgetProps {
  kind: string;
  widgetSource: WidgetMenuItem;
  currentTab: string | null;
  onAction?: (action: string, value: string) => void;
  customWidthSpan?: AllowedSpan;
  handleClick?: (activeItem: ActiveWidgetItem) => void;
}

/**
 * SodWidget
 */
function SodWidget({
  kind,
  widgetSource,
  currentTab,
  onAction,
  customWidthSpan,
  handleClick,
}: SodWidgetProps): ReactElement {
  const l = useInvertedLocalize();
  const { isGlobalLoading } = useTypedSelector((state) => state.aag.global);

  const [data, setData] = useState<WidgetDataListItem[] | null>(null);

  const { handleFetchWidgets, isLoading, isError } = useGetWidget();

  const triggerHandleFetchWidgets = useCallback(() => {
    if (widgetSource.endpoint) {
      setData(null);

      handleFetchWidgets(widgetSource.endpoint)
        .then((response) => {
          const typedResponse = response as {
            widgetName: string;
            top5Objects?: WidgetDataListItem[];
          };

          setData(typedResponse.top5Objects ?? []);
        })
        .catch(() => {
          Logger.log('Error loading the widget', widgetSource.label);

          setData(null);
        });
    }
  }, [handleFetchWidgets, widgetSource.endpoint, widgetSource.label]);

  useEffect(() => {
    if (!isGlobalLoading) {
      triggerHandleFetchWidgets();
    }
  }, [isGlobalLoading, triggerHandleFetchWidgets]);

  const wrapperClasses = cx(
    'Sod-Widget-wrapper',
    `Sod-Widget-wrapper-${kind}`,
    customWidthSpan && `Sod-Widget-wrapper-${customWidthSpan}`
  );

  const renderContentBody = useCallback(() => {
    if (isError) {
      return (
        <Box tag='div' className='Sod-Widget-errorWrapper'>
          <Box tag='div' className='Sod-Widget-errorMessage'>
            <Icon kind='StopProcess' size='large' color={CF_CRT_MED} />
            <Typography kind='body2' color={CF_CRT_MED}>
              {l("Data couldn't be retrieved.")}
            </Typography>
          </Box>
          <Button
            kind='filled'
            size='small'
            onClick={() => triggerHandleFetchWidgets()}>
            {l('Refresh')}
          </Button>
        </Box>
      );
    }

    if (isLoading || data === null) {
      return (
        <Box tag='div' className='Sod-Widget-loadingWrapper'>
          <Loader kind='dots' size='small' format='inline' color='brand' />
        </Box>
      );
    }

    if (Array.isArray(data) && data.length === 0) {
      return (
        <Box tag='div' className='Sod-Widget-noDataWrapper'>
          <Box tag='div' className='Sod-Widget-errorMessage'>
            <Icon kind='AlertInfo' size='large' color={CF_INF_MED} />
            <Typography kind='body2' color={CF_NEU_BLD}>
              {l('Data not found')}
            </Typography>
            <Typography kind='body3' color={CF_NEU_STL}>
              {l('Try updating the filters or check back later.')}
            </Typography>
          </Box>
        </Box>
      );
    }

    return data.map((item) => (
      <SodWidgetListItem
        key={item.id}
        kind={widgetSource.kind}
        tab={currentTab}
        iconString={
          widgetSource.kind === WIDGET_KINDS.ENDPOINT
            ? item.additionalInfo?.applicationLogo
            : widgetSource.icon
        }
        text={item.name}
        count={item.count}
        onClick={() => {
          if (handleClick) {
            handleClick({ value: widgetSource.value, data: item });
          }
        }}
      />
    ));
  }, [
    l,
    isError,
    isLoading,
    data,
    currentTab,
    widgetSource.icon,
    widgetSource.kind,
    widgetSource.value,
    handleClick,
    triggerHandleFetchWidgets,
  ]);

  return (
    <Box tag='div' className={wrapperClasses}>
      <Box className='Sod-Widget-headerWrapper'>
        <Box tag='div' className='Sod-Widget-headerTitleWrapper'>
          <Typography
            kind='h3'
            lineLimit={1}
            color={CF_SEC_BLD}
            showTooltipOnHover={false}
            className='Sod-Widget-headerTitle'>
            {widgetSource.label}
          </Typography>
        </Box>
        <Box className='Sod-Widget-hoverables'>
          <Menu
            options={Object.values(WIDGET_ACTION).map((action) => ({
              ...action,
              label: l(action.label),
            }))}
            onChange={(config: WidgetAction) => {
              if (onAction) {
                onAction(config.value, widgetSource.value);
              }
            }}
            placement='bottom-end'
            trigger={
              <ButtonIcon
                size='medium'
                kind='ghost'
                ariaLabel={l('more')}
                icon={
                  <Icon kind='MoreVertical' color='neutral-100' size='medium' />
                }
              />
            }
          />
        </Box>
      </Box>
      <Box tag='div' className='Sod-Widget-contentWrapper'>
        {renderContentBody()}
      </Box>
      {/* View All page is not created */}
      {/* <Box tag='div' className='Sod-Widget-footerWrapper'>
        <DSLink
          text='View All'
          kind='button'
          onClick={() => {
            Logger.log('View all pressed');
          }}
          suffixIconKind='ArrowRight'
          size='medium'
        />
      </Box> */}
    </Box>
  );
}

SodWidget.defaultProps = {
  onAction: () => {},
  handleClick: () => {},
  customWidthSpan: null,
};

export default memo(SodWidget);
